import { z } from "zod";

/**
 * `bot_versions.theme` — the ~12 inputs an agency sets per bot. This is the
 * only shape @cos/theme's derivation function accepts, and the only bot-version
 * data the widget is ever sent (see PLAN.md "Theming" and "Realtime design").
 * Deriving the same ~40 tokens from this input happens independently in the
 * panel (live preview), the widget (applying it) and the API (contrast
 * validation at save time) — one pure function, three call sites, so it's
 * kept here rather than duplicated.
 */
export const themeInputSchema = z.object({
  preset: z.enum(["rounded", "square", "minimal"]),
  /** #rrggbb — a single brand color, everything else is derived from it. */
  brand: z.string().regex(/^#[0-9a-fA-F]{6}$/, "brand must be a #rrggbb hex color"),
  surface: z.enum(["auto", "light", "dark"]),
  radius: z.enum(["sm", "md", "lg", "full"]),
  density: z.enum(["comfortable", "compact"]),
  font: z.union([
    z.enum(["system", "inherit"]),
    z.object({ family: z.string(), url: z.string().url() }),
  ]),
  launcher: z.object({
    position: z.enum(["bottom-right", "bottom-left"]).default("bottom-right"),
    offset: z.object({ x: z.number(), y: z.number() }).optional(),
    icon: z.string().optional(),
    label: z.string().optional(),
  }),
});

export type ThemeInput = z.infer<typeof themeInputSchema>;

/** A conservative default — a fresh bot needs a valid theme before anyone's touched the editor. */
export const defaultThemeInput: ThemeInput = {
  preset: "rounded",
  brand: "#0F7A5A",
  surface: "auto",
  radius: "md",
  density: "comfortable",
  font: "system",
  launcher: { position: "bottom-right" },
};
